import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, FileText, Camera, ExternalLink, BookOpen, MessageSquare, FolderOpen, Paperclip, ChevronRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../lib/api";
import { formatDate, formatDateTime } from "../../lib/format";
import clienteApi from "../../lib/clienteApi";
import MessageThread from "../../components/MessageThread";
import { AllegatoThumb, AllegatoLightbox } from "./ClienteAllegatoPreview";

const STATO = { aperta: "Aperta", in_lavorazione: "In lavorazione", legale: "Legale", mediazione: "Mediazione", chiusa: "Chiusa", out: "S.S." };
const TABS = [
  ["diario", "Diario", BookOpen, "aggiornamenti"],
  ["messaggi", "Messaggi", MessageSquare, "non letti"],
  ["documenti", "Documenti", FolderOpen, "documenti"],
  ["allegati", "Allegati", Paperclip, "file caricati"],
];

const SectionButton = ({ k, label, Icon, hint, count, onOpen }) => (
  <button type="button" onClick={() => onOpen(k)} data-testid={`cliente-tab-${k}`}
    className="w-full flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-4 min-h-[64px] text-left shadow-sm hover:border-amber-400 hover:bg-amber-50 active:scale-[0.99] transition-[background-color,border-color,transform]">
    <span className="h-11 w-11 shrink-0 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><Icon className="h-5 w-5" /></span>
    <span className="flex-1 min-w-0">
      <span className="block font-head text-base font-semibold text-[#111827]">{label}</span>
      <span className="block text-xs text-slate-500">{count == null ? "…" : `${count} ${hint}`}</span>
    </span>
    {count > 0 && (
      <span data-testid={`cliente-tab-count-${k}`}
        className={`min-w-[28px] h-7 px-2 rounded-full text-sm font-bold flex items-center justify-center ${k === "messaggi" ? "bg-[#dc2626] text-[#ffffff]" : "bg-amber-500 text-[#0a0d14]"}`}>{count}</span>
    )}
    <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />
  </button>
);

const Documenti = ({ pid }) => {
  const [items, setItems] = useState(null);
  const [opening, setOpening] = useState("");
  useEffect(() => { clienteApi.get(`/cliente/pratiche/${pid}/documenti`).then(({ data }) => setItems(data)).catch(() => setItems([])); }, [pid]);
  const open = async (d) => {
    const win = window.open("", "_blank");
    setOpening(d.id);
    try {
      const { data } = await clienteApi.get(`/cliente/pratiche/${pid}/documenti/${d.id}/content`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: d.content_type || "application/octet-stream" }));
      if (win) win.location.href = url; else window.location.href = url;
    } catch (e) { if (win) win.close(); toast.error("Impossibile aprire il documento"); } finally { setOpening(""); }
  };
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-amber-500" />;
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="cliente-documenti-empty">Lo studio non ha ancora condiviso documenti.</p>;
  return (
    <ul className="divide-y divide-slate-200" data-testid="cliente-documenti-list">
      {items.map((d) => (
        <li key={d.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`cliente-documento-${d.id}`}>
          <span className="flex items-center gap-2 text-sm text-slate-700 min-w-0"><FileText className="h-4 w-4 text-amber-600 shrink-0" /><span className="truncate">{d.nome}</span></span>
          <button onClick={() => open(d)} disabled={opening === d.id} data-testid={`cliente-documento-open-${d.id}`}
            className="flex items-center gap-1 shrink-0 rounded-md border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 transition-colors">
            {opening === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}Apri
          </button>
        </li>
      ))}
    </ul>
  );
};

const Diario = ({ pid }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { clienteApi.get(`/cliente/pratiche/${pid}/diario`).then(({ data }) => setItems(data)).catch(() => setItems([])); }, [pid]);
  if (!items) return <Loader2 className="h-5 w-5 animate-spin text-amber-500" />;
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="cliente-diario-empty">Nessun aggiornamento disponibile.</p>;
  return (
    <ol className="space-y-3" data-testid="cliente-diario-list">
      {items.map((d) => (
        <li key={d.id} className="border-l-2 border-amber-500 pl-4" data-testid={`cliente-diario-${d.id}`}>
          <div className="text-xs font-mono text-slate-500">{formatDate(d.data)} {d.ora || ""}</div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap">{d.descrizione}</div>
        </li>
      ))}
    </ol>
  );
};

const Allegati = ({ pid }) => {
  const [items, setItems] = useState([]);
  const [tipo, setTipo] = useState("documento");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState([]);
  const [doneMsg, setDoneMsg] = useState("");
  const [preview, setPreview] = useState(null);
  const ref = useRef(null);
  const load = () => clienteApi.get(`/cliente/pratiche/${pid}/allegati`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const upload = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setBusy(true); setDoneMsg("");
    setProgress(files.map((f) => ({ name: f.name, pct: 0, status: "pending" })));
    const upd = (i, patch) => setProgress((p) => p.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    let ok = 0;
    await Promise.all(files.map(async (f, i) => {
      const fd = new FormData(); fd.append("file", f); fd.append("tipo", tipo);
      try {
        await clienteApi.post(`/cliente/pratiche/${pid}/allegati`, fd, {
          onUploadProgress: (ev) => upd(i, { pct: ev.total ? Math.min(99, Math.round((ev.loaded / ev.total) * 100)) : 50 }),
        });
        upd(i, { pct: 100, status: "ok" }); ok += 1;
      } catch (err) { upd(i, { status: "error", error: formatApiError(err.response?.data?.detail) }); }
    }));
    setBusy(false); load();
    if (ok === files.length) setDoneMsg("Allegato/i inviati con successo");
    else if (ok > 0) setDoneMsg(`${ok} di ${files.length} allegati inviati con successo`);
  };
  return (
    <div className="space-y-4" data-testid="cliente-allegati">
      <div className="flex flex-wrap items-center gap-2">
        {[["documento", "Documento", FileText], ["foto_danno", "Foto danno veicolo", Camera]].map(([v, l, I]) => (
          <button key={v} type="button" onClick={() => setTipo(v)} data-testid={`cliente-allegato-tipo-${v}`}
            className={`flex flex-1 sm:flex-none justify-center items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors ${tipo === v ? "border-amber-500 bg-amber-50 text-amber-800" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
            <I className="h-4 w-4 shrink-0" /><span className="whitespace-nowrap">{v === "foto_danno" ? <>Foto danno<span className="hidden sm:inline"> veicolo</span></> : l}</span>
          </button>
        ))}
        <input ref={ref} type="file" multiple accept="image/*,application/pdf" capture={tipo === "foto_danno" ? "environment" : undefined} className="hidden" onChange={upload} data-testid="cliente-allegato-input" />
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="cliente-allegato-upload"
          className="flex w-full sm:w-auto justify-center items-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 px-4 py-2 text-sm font-semibold text-[#0a0d14] transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Carica
        </button>
      </div>
      <p className="text-xs text-slate-500">Immagini o PDF, massimo 20MB per file. Lo studio li riceverà nella pratica.</p>
      {progress.length > 0 && (
        <ul className="space-y-2" data-testid="cliente-upload-progress">
          {progress.map((p, i) => (
            <li key={i} className="text-xs" data-testid={`cliente-upload-progress-${i}`}>
              <div className="flex justify-between gap-2 mb-1">
                <span className="truncate text-slate-700">{p.name}</span>
                <span className={`shrink-0 font-mono ${p.status === "error" ? "text-red-600" : p.status === "ok" ? "text-green-600" : "text-slate-500"}`}>
                  {p.status === "error" ? "Errore" : `${p.pct}%`}
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                <div className={`h-full rounded-full transition-[width] duration-300 ${p.status === "error" ? "bg-red-500" : p.status === "ok" ? "bg-green-500" : "bg-amber-500"}`}
                  style={{ width: `${p.status === "error" ? 100 : p.pct}%` }} />
              </div>
              {p.error && <div className="text-red-600 mt-0.5">{p.error}</div>}
            </li>
          ))}
        </ul>
      )}
      {doneMsg && (
        <div className="rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-sm font-semibold text-green-700" role="status" data-testid="cliente-upload-success">{doneMsg}</div>
      )}
      {items.length === 0 ? <p className="text-sm text-slate-500" data-testid="cliente-allegati-empty">Nessun file caricato.</p> : (
        <ul className="divide-y divide-slate-200" data-testid="cliente-allegati-list">
          {items.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`cliente-allegato-${a.id}`}>
              <span className="flex items-center gap-3 min-w-0">
                <AllegatoThumb pid={pid} a={a} onOpen={setPreview} />
                <span className="text-slate-700 truncate">{a.nome}</span>
              </span>
              <span className="text-xs font-mono text-slate-500 shrink-0 ml-3">{formatDateTime(a.created_at)}</span>
            </li>
          ))}
        </ul>
      )}
      <AllegatoLightbox pid={pid} item={preview} onClose={() => setPreview(null)} />
    </div>
  );
};

export const ClientePraticaView = ({ pratica, initialTab = null }) => {
  const [tab, setTab] = useState(initialTab);
  const [counts, setCounts] = useState({});
  useEffect(() => {
    const load = () => clienteApi.get(`/cliente/pratiche/${pratica.id}/conteggi`).then(({ data }) => setCounts(data)).catch(() => {});
    load();
    if (tab) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [pratica.id, tab]);
  const current = TABS.find(([k]) => k === tab);
  return (
    <div className="space-y-4 sm:space-y-6">
      <section className="pgp-card p-4 sm:p-6 grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="cliente-pratica-stato">
        <div className="col-span-2 sm:col-span-1"><div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Numero pratica</div><div className="font-head text-lg font-bold break-all" data-testid="cliente-pratica-numero">{pratica.numero_pratica}</div></div>
        <div><div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Stato attuale</div>
          <span className="inline-block mt-1 rounded-full bg-amber-100 text-amber-800 px-3 py-0.5 text-sm font-semibold" data-testid="cliente-pratica-stato-label">{STATO[pratica.stato] || pratica.stato || "—"}</span></div>
        <div><div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Data apertura</div><div className="text-lg" data-testid="cliente-pratica-apertura">{formatDate(pratica.data_apertura || pratica.created_at)}</div></div>
      </section>
      {!tab ? (
        <nav className="flex flex-col gap-3" data-testid="cliente-sezioni">
          {TABS.map(([k, l, I, h]) => <SectionButton key={k} k={k} label={l} Icon={I} hint={h} count={counts[k]} onOpen={setTab} />)}
        </nav>
      ) : (
      <section className="pgp-card p-3 sm:p-6 min-w-0" data-testid={`cliente-sezione-${tab}`}>
        <div className="flex items-center gap-2 border-b border-slate-200 pb-3 mb-4 sm:mb-5">
          <button type="button" onClick={() => setTab(null)} data-testid="cliente-sezione-back"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-[#111827] hover:bg-slate-100 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Indietro
          </button>
          <h2 className="font-head text-base font-semibold text-[#111827]">{current?.[1]}</h2>
        </div>
        {tab === "diario" && <Diario pid={pratica.id} />}
        {tab === "messaggi" && <MessageThread client={clienteApi} getUrl={`/cliente/pratiche/${pratica.id}/messages`} postUrl={`/cliente/pratiche/${pratica.id}/messages`} selfRole="cliente" />}
        {tab === "documenti" && <Documenti pid={pratica.id} />}
        {tab === "allegati" && <Allegati pid={pratica.id} />}
      </section>
      )}
    </div>
  );
};
